import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useJournal, type UseJournalReturn } from "../hooks/useJournal";

const JournalContext = createContext<UseJournalReturn | null>(null);

export function JournalProvider({ children }: { children: ReactNode }) {
  const journal = useJournal();

  const value = useMemo(
    () => journal,
    [
      journal.entries,
      journal.setEntries,
      journal.handleCreated,
      journal.handleUpdated,
      journal.handleDeleted,
      journal.handleOwnerChanged,
      journal.handleRevealed,
      journal.handlePinned,
      journal.handlePersonalCreated,
      journal.handleLinked,
      journal.handleUnlinked,
      journal.createEntry,
      journal.deleteEntry,
      journal.revealEntry,
      journal.pinEntry,
      journal.createPersonalNote,
    ],
  );

  return (
    <JournalContext.Provider value={value}>{children}</JournalContext.Provider>
  );
}

export function useJournalContext(): UseJournalReturn {
  const ctx = useContext(JournalContext);
  if (!ctx)
    throw new Error("useJournalContext must be used within JournalProvider");
  return ctx;
}

/**
 * Non-throwing variant for components that also render outside the journal
 * tree (unit tests render the timeline panel without providers). Null there.
 */
export function useOptionalJournalContext(): UseJournalReturn | null {
  return useContext(JournalContext);
}
